import React, { type FC, type ReactNode } from 'react';

import { cssMap } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled/box';

import { expVal } from '@confluence/feature-experiments';
import { ProfileCardWrapper } from '@confluence/profile/entry-points/ProfileCardWrapper.ts';

const styles = cssMap({
	triggerWrapper: {
		display: 'inline-block',
		cursor: 'pointer',
	},
	// Used when the trigger spans a whole row, so it keeps growing and allows its
	// text children to truncate rather than collapsing to content width.
	fullWidthTriggerWrapper: {
		display: 'flex',
		alignItems: 'center',
		flexGrow: 1,
		minWidth: '0px',
		cursor: 'pointer',
	},
});

export type AccessRequestProfileCardTriggerProps = {
	/** Atlassian Account ID of the requesting user. When falsy, children render unwrapped. */
	userId: string | null | undefined;
	/** Display name used as the fallback label inside the profile card. */
	displayName: string;
	/** Children to wrap with the profile hover card. */
	children: ReactNode;
	/**
	 * Offset of the profile card popup relative to the trigger, as [x, y] in pixels.
	 * Defaults to no offset. Use e.g. [0, -16] to shift the card upward.
	 */
	offset?: [number, number];
	/** Stretch the trigger to fill its row instead of hugging its content. */
	fullWidth?: boolean;
	/**
	 * Which hit-target layout this trigger belongs to:
	 * - `always` (default) — active whenever the profile card is available.
	 * - `row` — active only when avatar, name and email share one trigger.
	 * - `element` — active only when the avatar and name are separate triggers.
	 *
	 * Surfaces that offer both layouts render both triggers and let this prop pick one.
	 */
	hitTarget?: 'always' | 'row' | 'element';
};

/**
 * Wraps `children` in the standard Confluence profile hover card when `userId`
 * is present. Renders `children` unchanged when `userId` is missing so callers
 * can use this component unconditionally.
 */
export const AccessRequestProfileCardTrigger: FC<AccessRequestProfileCardTriggerProps> = ({
	userId,
	displayName,
	children,
	offset,
	fullWidth,
	hitTarget = 'always',
}) => {
	if (!userId) {
		return <>{children}</>;
	}

	if (
		hitTarget !== 'always' &&
		(hitTarget === 'row') !== expVal('confluence_access_request_updates', 'isEnabled', false)
	) {
		return <>{children}</>;
	}

	return (
		<Box xcss={fullWidth ? styles.fullWidthTriggerWrapper : styles.triggerWrapper}>
			<ProfileCardWrapper
				userId={userId}
				fullName={displayName}
				trigger={
					expVal('confluence_access_request_updates', 'isEnabled', false) ? 'click' : 'hover'
				}
				offset={offset}
			>
				{children}
			</ProfileCardWrapper>
		</Box>
	);
};
